import React, { useState, useEffect } from 'react'
import { useUIStore } from '../store/ui-store'
import { usePlayerStore } from '../store/player-store'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface ClubInfo {
  id: string
  name: string
  description?: string
  memberCount: number
  role?: string
}

export default function ClubsScreen(): React.ReactElement {
  const { navigateTo, showToast } = useUIStore()
  const { token } = usePlayerStore()
  const [myClubs, setMyClubs] = useState<ClubInfo[]>([])
  const [mode, setMode] = useState<'list' | 'create' | 'search'>('list')
  const [newClubName, setNewClubName] = useState('')
  const [newClubDesc, setNewClubDesc] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ClubInfo[]>([])
  const [joinClubId, setJoinClubId] = useState('')

  useEffect(() => {
    fetchMyClubs()
  }, [])

  const fetchMyClubs = async (): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/club/my`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (json.success) setMyClubs(json.data)
    } catch {}
  }

  const handleCreateClub = async (): Promise<void> => {
    if (!newClubName.trim()) return
    try {
      const res = await fetch(`${API_BASE}/club/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newClubName.trim(), description: newClubDesc.trim() }),
      })
      const json = await res.json()
      if (json.success) {
        showToast('Club created!', 'success')
        setMode('list')
        setNewClubName('')
        setNewClubDesc('')
        fetchMyClubs()
      } else {
        showToast(json.error || 'Failed to create club', 'error')
      }
    } catch {
      showToast('Failed to create club', 'error')
    }
  }

  const handleSearch = async (): Promise<void> => {
    if (!searchQuery.trim()) return
    try {
      const res = await fetch(`${API_BASE}/club/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (json.success) setSearchResults(json.data)
    } catch {}
  }

  const handleJoin = async (clubId: string): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/club/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clubId }),
      })
      const json = await res.json()
      if (json.success) {
        showToast('Joined club!', 'success')
        setMode('list')
        fetchMyClubs()
      } else {
        showToast(json.error || 'Failed to join', 'error')
      }
    } catch {
      showToast('Failed to join', 'error')
    }
  }

  const handleLeave = async (clubId: string): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/club/${clubId}/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (json.success) {
        showToast('Left club', 'info')
        fetchMyClubs()
      } else {
        showToast(json.error || 'Failed to leave', 'error')
      }
    } catch {
      showToast('Failed to leave', 'error')
    }
  }

  return (
    <div className="lobby">
      <div className="lobby-header">
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => navigateTo('lobby')}>
          ← Back
        </button>
        <div className="lobby-title">Clubs</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: 11 }} onClick={() => setMode(mode === 'search' ? 'list' : 'search')}>
            Search
          </button>
          <button className="btn btn-gold" style={{ padding: '6px 10px', fontSize: 11 }} onClick={() => setMode(mode === 'create' ? 'list' : 'create')}>
            {mode === 'create' ? 'Cancel' : 'Create'}
          </button>
        </div>
      </div>

      {mode === 'create' ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Create a Club</div>
          <input
            placeholder="Club name"
            maxLength={30}
            value={newClubName}
            onChange={e => setNewClubName(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14, marginBottom: 8,
            }}
          />
          <input
            placeholder="Description (optional)"
            maxLength={200}
            value={newClubDesc}
            onChange={e => setNewClubDesc(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14, marginBottom: 16,
            }}
          />
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={handleCreateClub}>
            Create Club
          </button>
        </div>
      ) : mode === 'search' ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Find Clubs</div>
          <input
            placeholder="Search by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14, marginBottom: 12,
            }}
          />
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={handleSearch}>
            Search
          </button>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Or join by ID:</div>
            <input
              placeholder="Paste club ID"
              value={joinClubId}
              onChange={e => setJoinClubId(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14, marginBottom: 8,
              }}
            />
            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => joinClubId && handleJoin(joinClubId)}>
              Join by ID
            </button>
          </div>

          {searchResults.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Results:</div>
              {searchResults.map(c => (
                <div key={c.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, marginBottom: 8,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{c.memberCount} members</div>
                  </div>
                  <button className="btn btn-gold" style={{ padding: '4px 12px', fontSize: 11 }} onClick={() => handleJoin(c.id)}>
                    Join
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {myClubs.length === 0 ? (
            <div className="flex-center" style={{ padding: 40, color: '#666', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 40 }}>👥</div>
              <div>No clubs yet</div>
              <button className="btn btn-gold" onClick={() => setMode('create')}>
                Create a Club
              </button>
              <button className="btn btn-secondary" onClick={() => setMode('search')}>
                Find Clubs
              </button>
            </div>
          ) : (
            myClubs.map(c => (
              <div key={c.id} style={{
                padding: '12px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 12, marginBottom: 12,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      {c.memberCount} members {c.role === 'owner' ? '· 👑 Owner' : ''}
                    </div>
                  </div>
                  {c.role !== 'owner' && (
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => handleLeave(c.id)}>
                      Leave
                    </button>
                  )}
                </div>
                {c.description && (
                  <div style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>{c.description}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
